import {
  DEFAULT_CHARACTER,
  resolveCharacter,
  BOT_TIERS,
  type BotTier,
  type CharacterId,
} from '@arena/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  DEFAULT_KEYBINDS,
  migrateRacialToSpace,
  normalizeKeybinds,
  type Keybinds,
} from '../game/input/bindings';
import { DEFAULT_AUDIO, normalizeAudio, type AudioSettings } from '../audio/volume';
import { clampZoom, MOBILE_ZOOM_DEFAULT } from '../game/cameraZoom';
import { isTouchOnlyDevice } from '../game/input/touchDevice';

export { DEFAULT_AUDIO, normalizeAudio, type AudioSettings };

export { DEFAULT_KEYBINDS, type Keybinds };

/** HUD scale range (spec §5: 80–150%). */
export const UI_SCALE_MIN = 0.8;
export const UI_SCALE_MAX = 1.5;

export function clampUiScale(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v)
    ? Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, v))
    : 1;
}

/**
 * Mobile camera zoom as stored: null means "use the default" (the player never moved the slider),
 * so a later change to MOBILE_ZOOM_DEFAULT reaches everyone without a migration (DROW-181).
 * Otherwise clamped and snapped to the slider's 5% steps.
 */
export function clampCameraZoom(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? clampZoom(v) : null;
}

/** The zoom the mobile camera uses. */
export const mobileCameraZoom = (s: { cameraZoomMobile: number | null }): number =>
  s.cameraZoomMobile ?? MOBILE_ZOOM_DEFAULT;

/** Overhead HP bars and names: everything, bars without names, or only your own bar. */
export const OVERHEAD_LABEL_MODES = ['all', 'bars', 'off'] as const;
export type OverheadLabelMode = (typeof OVERHEAD_LABEL_MODES)[number];

/**
 * Effects detail: Low halves spell particle counts and lightens the arena (fewer rim props and
 * scatter, one lava crack sample fewer) for weaker GPUs.
 */
export const VFX_QUALITIES = ['high', 'low'] as const;
export type VfxQuality = (typeof VFX_QUALITIES)[number];

/**
 * First-run effects quality (DROW-190): Low on a touch-only device, where the arena environment
 * budget is 40k triangles and 6 lava fetches; a saved choice always wins.
 */
export const defaultVfxQuality = (touchOnly = isTouchOnlyDevice()): VfxQuality =>
  touchOnly ? 'low' : 'high';

/** Home backdrop: the poster drifting, the poster still, or only the dark base colour. */
export const MENU_BACKGROUND_MODES = ['animated', 'static', 'off'] as const;
export type MenuBackgroundMode = (typeof MENU_BACKGROUND_MODES)[number];

/** Touch overlay (DROW-176): Auto draws it on a touch-only device. */
export const CONTROL_SCHEMES = ['auto', 'desktop', 'mobile'] as const;
export type ControlScheme = (typeof CONTROL_SCHEMES)[number];

/** What a tap (no drag) on a touch spell button aims at. */
export const TAP_ACTIONS = ['autoAim', 'facing'] as const;
export type TapAction = (typeof TAP_ACTIONS)[number];

/** Floating: the joystick base appears under the thumb. Fixed: it stays at its home spot. */
export const JOYSTICK_MODES = ['floating', 'fixed'] as const;
export type JoystickMode = (typeof JOYSTICK_MODES)[number];

export const JOYSTICK_SIZES = ['s', 'm', 'l'] as const;
export type JoystickSize = (typeof JOYSTICK_SIZES)[number];

const oneOf = <T extends string>(values: readonly T[], v: unknown, fallback: T): T =>
  values.includes(v as T) ? (v as T) : fallback;

const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

interface SettingsState {
  name: string;
  keybinds: Keybinds;
  /**
   * Cast on key press at the cursor instead of arming the spell and waiting for a click. On by
   * default, per the GDD.
   */
  quickCast: boolean;
  /** Last character picked in a lobby; sent as a join option so the pick carries over. */
  preferredCharacter: CharacterId;
  /** Last difficulty started from Play vs Bots; Easy until the player picks another. */
  botTier: BotTier;
  overheadLabels: OverheadLabelMode;
  /** Draw your own name over your warlock (off: you know who you are). */
  showOwnName: boolean;
  /** HUD size multiplier, UI_SCALE_MIN..UI_SCALE_MAX. */
  uiScale: number;
  vfxQuality: VfxQuality;
  menuBackground: MenuBackgroundMode;
  /** Go fullscreen when committing to a match (Ready / Start) and when it begins (DROW-179). */
  autoFullscreen: boolean;
  /** Volume sliders and mute; the audio engine follows these live. */
  audio: AudioSettings;
  controlScheme: ControlScheme;
  tapAction: TapAction;
  joystickMode: JoystickMode;
  joystickSize: JoystickSize;
  /** Left-handed: joystick on the right, spell buttons on the left. */
  mirrorControls: boolean;
  /** Vibrate on casts and big hits (Android; iOS Safari has no vibration API). */
  haptics: boolean;
  /** Mobile camera zoom; null until the player moves the slider (see `clampCameraZoom`). */
  cameraZoomMobile: number | null;
  /** The "Switch to touch controls?" toast was shown once; it never shows again. */
  touchPromptShown: boolean;
  setName: (name: string) => void;
  setQuickCast: (quickCast: boolean) => void;
  setPreferredCharacter: (id: CharacterId) => void;
  setBotTier: (tier: BotTier) => void;
  setOverheadLabels: (mode: OverheadLabelMode) => void;
  setShowOwnName: (show: boolean) => void;
  setUiScale: (scale: number) => void;
  setVfxQuality: (quality: VfxQuality) => void;
  setMenuBackground: (mode: MenuBackgroundMode) => void;
  setAutoFullscreen: (on: boolean) => void;
  /** Normalized on the way in, so what is stored is exactly what a reload restores. */
  setKeybinds: (keybinds: Keybinds) => void;
  resetKeybinds: () => void;
  /** Merges and validates, so an out-of-range slider value can't reach the engine. */
  setAudio: (patch: Partial<AudioSettings>) => void;
  toggleMute: () => void;
  setControlScheme: (scheme: ControlScheme) => void;
  setTapAction: (action: TapAction) => void;
  setJoystickMode: (mode: JoystickMode) => void;
  setJoystickSize: (size: JoystickSize) => void;
  setMirrorControls: (mirror: boolean) => void;
  setHaptics: (on: boolean) => void;
  setCameraZoomMobile: (zoom: number) => void;
  setTouchPromptShown: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      name: '',
      keybinds: DEFAULT_KEYBINDS,
      quickCast: true,
      preferredCharacter: DEFAULT_CHARACTER,
      botTier: 'easy',
      overheadLabels: 'all',
      showOwnName: false,
      uiScale: 1,
      vfxQuality: defaultVfxQuality(),
      menuBackground: 'animated',
      autoFullscreen: true,
      audio: DEFAULT_AUDIO,
      controlScheme: 'auto',
      tapAction: 'autoAim',
      joystickMode: 'floating',
      joystickSize: 'm',
      mirrorControls: false,
      haptics: true,
      cameraZoomMobile: null,
      touchPromptShown: false,
      setName: (name) => set({ name }),
      setQuickCast: (quickCast) => set({ quickCast }),
      setPreferredCharacter: (preferredCharacter) => set({ preferredCharacter }),
      setBotTier: (botTier) => set({ botTier }),
      setOverheadLabels: (overheadLabels) => set({ overheadLabels }),
      setShowOwnName: (showOwnName) => set({ showOwnName }),
      setUiScale: (uiScale) => set({ uiScale: clampUiScale(uiScale) }),
      setVfxQuality: (vfxQuality) => set({ vfxQuality }),
      setMenuBackground: (menuBackground) => set({ menuBackground }),
      setAutoFullscreen: (autoFullscreen) => set({ autoFullscreen }),
      setKeybinds: (keybinds) => set({ keybinds: normalizeKeybinds(keybinds) }),
      resetKeybinds: () => set({ keybinds: DEFAULT_KEYBINDS }),
      setAudio: (patch) => set((s) => ({ audio: normalizeAudio({ ...s.audio, ...patch }) })),
      toggleMute: () => set((s) => ({ audio: { ...s.audio, muted: !s.audio.muted } })),
      setControlScheme: (controlScheme) => set({ controlScheme }),
      setTapAction: (tapAction) => set({ tapAction }),
      setJoystickMode: (joystickMode) => set({ joystickMode }),
      setJoystickSize: (joystickSize) => set({ joystickSize }),
      setMirrorControls: (mirrorControls) => set({ mirrorControls }),
      setHaptics: (haptics) => set({ haptics }),
      setCameraZoomMobile: (zoom) => set({ cameraZoomMobile: clampCameraZoom(zoom) }),
      setTouchPromptShown: () => set({ touchPromptShown: true }),
    }),
    {
      name: 'arena.settings',
      // Bump only with a migrate: without one, zustand drops the whole blob (names included).
      version: 3,
      migrate: (persisted, version) => {
        const s = (persisted ?? {}) as Record<string, unknown>;
        // v1 put slots on Q..V and Stop on S, which clash with WASD: everyone gets the new
        // defaults once (DROW-118). Everything else carries over.
        if (version < 2) s.keybinds = DEFAULT_KEYBINDS;
        // v2 had the racial on T and free cam on Space: the racial moves to Space (DROW-155).
        else if (version < 3) s.keybinds = migrateRacialToSpace(s.keybinds);
        return s as unknown as SettingsState;
      },
      // A stale or removed character id, bot tier, label mode or effects quality falls back to the default instead of reaching
      // the UI.
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<SettingsState>;
        return {
          ...current,
          ...saved,
          keybinds: normalizeKeybinds(saved.keybinds),
          preferredCharacter: resolveCharacter(saved.preferredCharacter),
          botTier: BOT_TIERS.includes(saved.botTier as BotTier) ? saved.botTier! : current.botTier,
          overheadLabels: OVERHEAD_LABEL_MODES.includes(saved.overheadLabels as OverheadLabelMode)
            ? saved.overheadLabels!
            : current.overheadLabels,
          showOwnName:
            typeof saved.showOwnName === 'boolean' ? saved.showOwnName : current.showOwnName,
          uiScale: saved.uiScale === undefined ? current.uiScale : clampUiScale(saved.uiScale),
          vfxQuality: VFX_QUALITIES.includes(saved.vfxQuality as VfxQuality)
            ? saved.vfxQuality!
            : current.vfxQuality,
          menuBackground: MENU_BACKGROUND_MODES.includes(saved.menuBackground as MenuBackgroundMode)
            ? saved.menuBackground!
            : current.menuBackground,
          autoFullscreen: bool(saved.autoFullscreen, current.autoFullscreen),
          // Older blobs have no `audio` and get the defaults, so this needs no version bump.
          audio: normalizeAudio(saved.audio),
          // Touch settings (DROW-176): older blobs have none and get the defaults.
          controlScheme: oneOf(CONTROL_SCHEMES, saved.controlScheme, current.controlScheme),
          tapAction: oneOf(TAP_ACTIONS, saved.tapAction, current.tapAction),
          joystickMode: oneOf(JOYSTICK_MODES, saved.joystickMode, current.joystickMode),
          joystickSize: oneOf(JOYSTICK_SIZES, saved.joystickSize, current.joystickSize),
          mirrorControls: bool(saved.mirrorControls, current.mirrorControls),
          haptics: bool(saved.haptics, current.haptics),
          // Older blobs have no key: null, the default (DROW-181).
          cameraZoomMobile: clampCameraZoom(saved.cameraZoomMobile),
          touchPromptShown: bool(saved.touchPromptShown, current.touchPromptShown),
        };
      },
    },
  ),
);
